/**
 * What Jiva knew about the patient after one exchange, and what that exchange changed.
 *
 * Read off the backend's replay (`cumulative_so_far`, `learned_this_turn`): confirmed values
 * only, keyed by graph relation, in the order they were first learned. Inferred rows count —
 * med-reasoning filled their values in and the graph moved on them — so "before" is the row
 * immediately before this one, whatever kind it is.
 *
 * Each value carries one face, the eval dashboard's encodings: `new` when this exchange added
 * it (blue, "related to what you are looking at"), `again` when this exchange mapped a value
 * that was already known (dotted, "carried again"), `held` otherwise.
 */

import type { TurnView } from "../../api/types";
import { inTurnOrder } from "./liveBeliefs";

export type KnownFace = "new" | "again" | "held";

export type KnownValue = { value: string; face: KnownFace };

export type KnownRow = { relation: string; values: KnownValue[] };

type KnownTurn = Pick<TurnView, "turn_number" | "cumulative_so_far" | "learned_this_turn">;

function has(map: Record<string, string[]> | undefined, relation: string, value: string): boolean {
  return map?.[relation]?.includes(value) ?? false;
}

/** The known values after the row at `turnNumber`; `[]` when the row is unknown. */
export function knownSoFar(turns: readonly KnownTurn[], turnNumber: number): KnownRow[] {
  const ordered = inTurnOrder(turns);
  const at = ordered.findIndex((turn) => turn.turn_number === turnNumber);
  if (at === -1) return [];
  const row = ordered[at];
  const before = at > 0 ? ordered[at - 1].cumulative_so_far : {};
  return Object.entries(row.cumulative_so_far ?? {})
    .filter(([, values]) => values.length > 0)
    .map(([relation, values]) => ({
      relation,
      values: values.map((value) => ({
        value,
        face: !has(row.learned_this_turn, relation, value)
          ? "held"
          : has(before, relation, value)
            ? "again"
            : "new",
      })),
    }));
}
